import { ArrowRight, ClipboardText, Package, Storefront } from 'shared/icons';
import Badge from '../../ui/Badge';
import { Mono } from '../../ui/Mono';
import { DescribeOrigin, IsPurchaseOrderOrigin, OriginVariant } from './productionModel';

/**
 * A PONTA DA GENEALOGIA: pallet consumido → ordem de compra → FORNECEDOR.
 *
 * ── Por que este componente existe ──────────────────────────────────────────
 * A cadeia sempre esteve no dado (`wms_lot_consumptions.lpn_id` →
 * `wms_lpns.origin_type`/`origin_id` → `purchase_orders`) e a tela parava no
 * lote do fornecedor, mandando o leitor "ver no módulo de Compras". Num recall
 * isso é a diferença entre responder e abrir outra aba. O mesmo desenho aparece
 * em três lugares (árvore de origem, lista de consumos e a busca pelo lote do
 * fornecedor), então ele mora aqui uma vez só.
 *
 * ── A regra que ele carrega ─────────────────────────────────────────────────
 * Origem que NÃO é compra aparece DIZENDO o que é — nunca em branco, e nunca
 * com cara de compra. Quem decide isso é o backend (`lpn_origin.resolution`),
 * que tem um veredito para cada caso: sem pallet, pallet sumido do cadastro,
 * pallet sem origem, compra resolvida, compra órfã e origem de outro tipo. Esta
 * tela só escolhe o ícone, a cor e a ordem de leitura.
 */
const OriginChain = ({ origin, label, compact = false }) => {
    if (!origin) {
        return null;
    }

    const isPurchase = IsPurchaseOrderOrigin(origin);
    const purchaseOrder = origin.purchase_order ?? null;
    const supplier = origin.supplier ?? null;

    return (
        <div className="space-y-1.5">
            {/* 1º elo: O PALLET. É a pergunta literal do dono — "qual LPN estava
                sendo consumido naquele lote". Sem pallet, o veredito abaixo
                explica (bipe direto na bobina é fluxo legítimo). */}
            {origin.lpn_code ? (
                <div className="flex items-center gap-1.5">
                    <Package size={14} className="shrink-0 text-mono-500" />
                    <Mono className="text-xs text-black dark:text-white">{origin.lpn_code}</Mono>
                    {origin.lpn_location_code ? (
                        <span className="truncate text-xs text-mono-500 dark:text-mono-400">
                            · {origin.lpn_location_code}
                        </span>
                    ) : null}
                </div>
            ) : null}

            {isPurchase ? (
                <>
                    {/* 2º elo: A ORDEM DE COMPRA. */}
                    <div className="flex items-center gap-1.5">
                        <ArrowRight size={12} className="shrink-0 text-mono-400" />
                        <ClipboardText size={14} className="shrink-0 text-mono-500" />
                        <span className="text-xs text-mono-500 dark:text-mono-400">
                            {label('wms.production.origin_purchase_order')}
                        </span>
                        <Mono className="text-xs font-medium text-black dark:text-white">
                            {purchaseOrder?.code ?? '—'}
                        </Mono>
                    </div>
                    {/* 3º elo: O FORNECEDOR — o fim da cadeia. Fornecedor apagado
                        do cadastro não some daqui: o nome vem nulo e o id
                        continua respondendo de quem foi a compra. */}
                    <div className="flex items-center gap-1.5">
                        <ArrowRight size={12} className="shrink-0 text-mono-400" />
                        <Storefront size={14} className="shrink-0 text-mono-500" />
                        <span className="text-xs text-mono-500 dark:text-mono-400">
                            {label('wms.production.origin_supplier')}
                        </span>
                        <span className="truncate text-xs font-medium text-black dark:text-white">
                            {supplier?.name || <Mono>{supplier?.organization_id ?? '—'}</Mono>}
                        </span>
                    </div>
                </>
            ) : (
                // NÃO É COMPRA: a frase do veredito, sempre. Em modo compacto
                // (linha da árvore) o texto vai solto; fora dele, num badge que
                // separa visualmente "cadeia fechada" de "cadeia interrompida".
                <div className="flex items-start gap-1.5">
                    <ArrowRight size={12} className="mt-0.5 shrink-0 text-mono-400" />
                    {compact ? (
                        <span className="text-xs text-mono-500 dark:text-mono-400">
                            {DescribeOrigin(origin, label)}
                        </span>
                    ) : (
                        <Badge variant={OriginVariant(origin)}>{DescribeOrigin(origin, label)}</Badge>
                    )}
                </div>
            )}

            {/* O id cru da origem quando ele NÃO resolveu: é o que quem for
                investigar o vínculo órfão precisa copiar. */}
            {!isPurchase && origin.origin_id ? (
                <Mono className="block break-all pl-5 text-[11px] text-mono-400">{origin.origin_id}</Mono>
            ) : null}
        </div>
    );
};

export default OriginChain;
